import { useEffect, useCallback } from 'react'
import { type FormikErrors, type FormikConfig } from 'formik'
import {
  useReportRequestCreateMutation,
  ListReportRequestsDocument,
  ReportRequestAssociatedObjectTypeEnum,
} from '__codegen__/api'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useDashIntl } from 'intl/intl.hooks'
import { useNotifications } from 'legacy/common/utils/notifications/notifications.hooks'
import { trackEvent } from 'utils/events/trackEvent'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { type GeneratedReportsAddFormValues } from 'legacy/sections/generated-reports/components/add/components/form/utils/generatedReportsAddForm.types'
import { type UseGeneratedReportsAddBehaviour } from 'legacy/sections/generated-reports/components/add/utils/generatedReportsAdd.hooks'
import {
  generatedReportsFilterFormKeyMapping,
  generatedReportsFilterCheckValidator,
  generatedReportsFilterErrorMessages,
} from 'legacy/sections/generated-reports/components/add/components/form/filters/utils/generatedReportsAddFormFilters.utils'
import { prepareFiltersForOnSubmit } from 'legacy/sections/generated-reports/components/add/components/form/utils/generatedReportsAddForm.utils'
import { type DateSelectRangeValue } from 'legacy/dash-components/date-select-range/utils/dateSelectRange.hooks'

interface UseCreateReportRequestProps {
  onSuccess: () => void
}

type GeneratedReportsAddFormWrapperOnSubmit =
  FormikConfig<GeneratedReportsAddFormValues>['onSubmit']

type GeneratedReportsAddFormWrapperValidate =
  FormikConfig<GeneratedReportsAddFormValues>['validate']

type FilterOptions = UseGeneratedReportsAddBehaviour['filterOptions']

export const useGeneratedReportsValidate = (
  filterOptions: FilterOptions
): GeneratedReportsAddFormWrapperValidate => {
  const intl = useDashIntl()

  return useCallback(
    values => {
      const errors: FormikErrors<GeneratedReportsAddFormValues> = {}

      const associatedReportFilters = filterOptions[values.reportType]

      if (!values.reportName) {
        errors.reportName = intl.formatMessage({
          id: 'generatedReports.newReport.formFields.reportName.error.required',
        })
      }

      if (!values.reportType) {
        errors.reportType = intl.formatMessage({
          id: 'generatedReports.newReport.formFields.reportType.error.required',
        })
      }

      if (associatedReportFilters) {
        associatedReportFilters.forEach(filter => {
          const key = generatedReportsFilterFormKeyMapping(filter)

          if (filter.required) {
            const isValid = generatedReportsFilterCheckValidator(filter, values)

            if (!isValid) {
              errors[key] = intl.formatMessage({
                id: generatedReportsFilterErrorMessages[key],
              }) as FormikErrors<DateSelectRangeValue>
            }
          }
        })
      }

      return errors
    },
    [filterOptions, intl]
  )
}

const useCreateReportRequest = ({ onSuccess }: UseCreateReportRequestProps) => {
  const intl = useDashIntl()
  const retailerId = useRetailerId()
  const { notify, notifyError } = useNotifications()
  const goToReportsList = useGoToPath('generated-reports')

  const [reportRequestCreateMutation, { data: createdReportRequest, error }] =
    useReportRequestCreateMutation({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: ListReportRequestsDocument,
          variables: {
            retailerId,
            limit: 25,
            offset: 0,
          },
        },
      ],
    })

  const success = Boolean(createdReportRequest)

  // On success, redirect them to the list page
  useEffect(() => {
    if (success) {
      trackEvent({
        id: 'generated_reports.newReport.submitSuccess',
        description: 'Successfully created a new report request',
      })
      notify(intl.formatMessage({ id: 'generatedReports.newReport.submit.success' }))
      onSuccess()
    }
  }, [success, goToReportsList, intl, notify, onSuccess])

  useEffect(() => {
    if (error) {
      trackEvent({
        id: 'generated_reports.newReport.submitFail',
        description: 'Was not able to create a report request',
      })
      notifyError(intl.formatMessage({ id: 'generatedReports.newReport.submit.fail' }))
    }
  }, [error, intl, notifyError])

  return {
    createReportRequest: reportRequestCreateMutation,
  }
}

export const useGeneratedReportsOnSubmit = (): GeneratedReportsAddFormWrapperOnSubmit => {
  const retailerId = useRetailerId()
  const goToReportsList = useGoToPath('generated-reports')
  const { createReportRequest } = useCreateReportRequest({ onSuccess: goToReportsList })

  return useCallback(
    ({ reportName, reportType, ...rest }, { setSubmitting }) => {
      const reportFilters = prepareFiltersForOnSubmit(rest)
      const variables = {
        name: reportName,
        reportId: reportType,
        associatedObjectId: retailerId,
        reportFilters,
        // Hard code object type to retailer until we get more types
        associatedObjectType: ReportRequestAssociatedObjectTypeEnum.Retailer,
      }

      setSubmitting(true)

      createReportRequest({ variables }).finally(() => {
        setSubmitting(false)
      })
    },
    [createReportRequest, retailerId]
  )
}
